// Mock Feed Data Service
// Provides mock data for development and testing

import { FeedItem, FeedFilters } from "../../types/socialFeed";
import { logger } from "../../utils/logger";

export class MockFeedData {
  static async getFeedItems(userId: string, filters: FeedFilters): Promise<FeedItem[]> {
    // Debug logging removed for cleaner console
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 300));

    const mockItems: FeedItem[] = [
      // Heart Games Items
      {
        id: "heart-games-1",
        type: "heart-games-achievement",
        userId: "user-1",
        username: "Alex",
        avatar: "/images/profile-alex.jpg",
        timestamp: new Date(Date.now() - 1000 * 60 * 30), // 30 minutes ago
        content: {
          title: "Achievement Unlocked!",
          description: "Completed Heart Choices with perfect score",
          gameType: "Heart Choices",
          achievement: {
            id: "achievement-1",
            name: "Heart Master",
            description: "Complete all Heart Choices scenarios",
            icon: "💎",
            rarity: "rare",
            unlockedAt: new Date(),
            category: "games",
            gameType: "Heart Choices",
            level: 5,
          },
          score: 95,
          level: 5,
        },
        engagement: {
          likes: 12,
          comments: 3,
          shares: 1,
          isLiked: false,
          isCommented: false,
          isShared: false,
        },
        metadata: {
          showSwipeOption: true,
          compatibilityScore: 85,
          priority: "high",
          tags: ["heart-games", "achievement"],
        },
      },
      {
        id: "heart-games-2",
        type: "heart-games-completion",
        userId: "user-2",
        username: "Jordan",
        avatar: "/images/profile-jordan.jpg",
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2), // 2 hours ago
        content: {
          title: "Game Completed!",
          description: "Just finished Heart Visions",
          gameType: "Heart Visions",
          score: 88,
          level: 3,
          gameResult: {
            gameId: "heart-visions-1",
            gameType: "Heart Visions",
            score: 88,
            completedAt: new Date(),
            duration: 180,
            answers: [],
            insights: ["You have a strong visual preference for nature scenes"],
          },
        },
        engagement: {
          likes: 8,
          comments: 2,
          shares: 0,
          isLiked: true,
          isCommented: false,
          isShared: false,
        },
        metadata: {
          showSwipeOption: false,
          compatibilityScore: 72,
          priority: "medium",
          tags: ["heart-games", "completion"],
        },
      },
      // Compatibility Items
      {
        id: "compatibility-1",
        type: "compatibility-suggestion",
        userId: "user-3",
        username: "Sam",
        avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&h=1200&fit=crop&crop=faces",
        timestamp: new Date(Date.now() - 1000 * 60 * 45), // 45 minutes ago
        content: {
          title: "Great Match Found!",
          description: "You have 92% compatibility",
          compatibilityScore: 92,
          matchReasons: [
            "Similar personality traits",
            "Shared love for adventure",
            "Compatible communication styles"
          ],
          sharedInterests: ["Travel", "Photography", "Cooking"],
          conversationStarters: [
            "What's your favorite travel destination?",
            "Do you enjoy trying new cuisines?"
          ],
          personalityAlignment: {
            mbtiCompatibility: 88,
            traitSimilarity: 85,
            traitComplementarity: 90,
            communicationStyle: 95,
          },
        },
        engagement: {
          likes: 15,
          comments: 5,
          shares: 2,
          isLiked: false,
          isCommented: false,
          isShared: false,
        },
        metadata: {
          showSwipeOption: true,
          compatibilityScore: 92,
          priority: "high",
          tags: ["compatibility", "match"],
        },
      },
      // Social Activity Items
      {
        id: "social-1",
        type: "social-activity",
        userId: "user-4",
        username: "Priya",
        avatar: "/images/couple-priya-alex.jpg",
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 3), // 3 hours ago
        content: {
          title: "New Update",
          description: "Just updated my profile with new photos from my recent trip to Japan!",
          media: {
            type: "image",
            url: "/images/feature-holographic-interface.jpg",
            thumbnail: "/images/feature-holographic-interface.jpg",
            aspectRatio: 16/9,
            alt: "Travel photos from Japan",
          },
        },
        engagement: {
          likes: 24,
          comments: 8,
          shares: 3,
          isLiked: true,
          isCommented: true,
          isShared: false,
        },
        metadata: {
          showSwipeOption: false,
          compatibilityScore: 78,
          priority: "medium",
          tags: ["social", "travel"],
          location: "Tokyo, Japan",
        },
      },
      // Community Challenge Items
      {
        id: "challenge-1",
        type: "community-challenge",
        userId: "user-5",
        username: "Marcus",
        avatar: "/images/couple-marcus-elena.jpg",
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 4), // 4 hours ago
        content: {
          title: "Weekly Challenge",
          description: "Complete 5 Heart Games this week",
          challenge: {
            id: "challenge-1",
            title: "Heart Games Marathon",
            description: "Complete 5 different Heart Games this week",
            type: "weekly",
            startDate: new Date(),
            endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
            participants: 156,
            reward: {
              xp: 500,
              badges: [],
              features: [],
              social: {
                profileHighlight: true,
                leaderboardPosition: 10,
                exclusiveContent: false,
                communityRecognition: true,
              },
            },
          },
        },
        engagement: {
          likes: 45,
          comments: 12,
          shares: 8,
          isLiked: false,
          isCommented: false,
          isShared: false,
        },
        metadata: {
          showSwipeOption: false,
          priority: "high",
          tags: ["challenge", "community"],
        },
      },
      // Match Announcement Items
      {
        id: "match-1",
        type: "match-announcement",
        userId: "user-6",
        username: "Sarah",
        avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=800&h=1200&fit=crop&crop=faces",
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 5), // 5 hours ago
        content: {
          title: "New Match!",
          description: "Matched with David",
          announcement: {
            id: "match-1",
            userId: "user-6",
            matchedUserId: "user-7",
            matchedAt: new Date(),
            compatibilityScore: 89,
            sharedInterests: ["Music", "Art", "Hiking"],
          },
        },
        engagement: {
          likes: 32,
          comments: 15,
          shares: 5,
          isLiked: true,
          isCommented: false,
          isShared: true,
        },
        metadata: {
          showSwipeOption: false,
          priority: "high",
          tags: ["match", "announcement"],
        },
      },
      // Streak Milestone Items
      {
        id: "streak-1",
        type: "streak-milestone",
        userId: "user-8",
        username: "Elena",
        avatar: "/images/couple-marcus-elena.jpg",
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 6), // 6 hours ago
        content: {
          title: "Streak Milestone!",
          description: "7 day streak achieved",
          streak: 7,
          gameType: "Heart Games",
        },
        engagement: {
          likes: 18,
          comments: 4,
          shares: 1,
          isLiked: false,
          isCommented: false,
          isShared: false,
        },
        metadata: {
          showSwipeOption: true,
          compatibilityScore: 81,
          priority: "medium",
          tags: ["streak", "milestone"],
        },
      },
      // Additional Profile Update Items
      {
        id: "profile-update-1",
        type: "profile-update",
        userId: "user-9",
        username: "Maya",
        avatar: "/images/profile-maya.jpg",
        timestamp: new Date(Date.now() - 1000 * 60 * 15), // 15 minutes ago
        content: {
          title: "Profile Updated",
          description: "Added new photos from my hiking trip in the mountains! 🏔️",
          media: {
            type: "image",
            url: "/images/feature-holographic-interface.jpg",
            thumbnail: "/images/feature-holographic-interface.jpg",
            aspectRatio: 4/3,
            alt: "Mountain hiking photos",
          },
          updateType: "photos",
          newPhotos: 3,
        },
        engagement: {
          likes: 28,
          comments: 6,
          shares: 2,
          isLiked: true,
          isCommented: false,
          isShared: false,
        },
        metadata: {
          showSwipeOption: true,
          compatibilityScore: 76,
          priority: "medium",
          tags: ["profile", "hiking", "nature"],
          location: "Rocky Mountains, CO",
        },
      },
      {
        id: "profile-update-2",
        type: "profile-update",
        userId: "user-10",
        username: "Chris",
        avatar: "/images/profile-chris.jpg",
        timestamp: new Date(Date.now() - 1000 * 60 * 25), // 25 minutes ago
        content: {
          title: "Bio Updated",
          description: "Just updated my bio to reflect my new passion for cooking! 👨‍🍳",
          updateType: "bio",
          oldBio: "Love traveling and photography",
          newBio: "Passionate chef, traveler, and photographer. Always looking for new recipes to try!",
        },
        engagement: {
          likes: 15,
          comments: 4,
          shares: 1,
          isLiked: false,
          isCommented: true,
          isShared: false,
        },
        metadata: {
          showSwipeOption: false,
          compatibilityScore: 83,
          priority: "low",
          tags: ["profile", "cooking", "bio"],
        },
      },
      // More Social Activity Posts
      {
        id: "social-2",
        type: "social-activity",
        userId: "user-11",
        username: "Taylor",
        avatar: "/images/profile-taylor.jpg",
        timestamp: new Date(Date.now() - 1000 * 60 * 35), // 35 minutes ago
        content: {
          title: "Just finished reading",
          description: "Just finished 'The Seven Husbands of Evelyn Hugo' - what an amazing book! 📚 Anyone else read it?",
          media: {
            type: "image",
            url: "/images/feature-holographic-interface.jpg",
            thumbnail: "/images/feature-holographic-interface.jpg",
            aspectRatio: 3/4,
            alt: "Book cover",
          },
          activityType: "reading",
          bookTitle: "The Seven Husbands of Evelyn Hugo",
        },
        engagement: {
          likes: 22,
          comments: 8,
          shares: 1,
          isLiked: false,
          isCommented: false,
          isShared: false,
        },
        metadata: {
          showSwipeOption: true,
          compatibilityScore: 79,
          priority: "medium",
          tags: ["reading", "books", "social"],
        },
      },
      {
        id: "social-3",
        type: "social-activity",
        userId: "user-12",
        username: "Riley",
        avatar: "/images/profile-riley.jpg",
        timestamp: new Date(Date.now() - 1000 * 60 * 50), // 50 minutes ago
        content: {
          title: "Coffee shop vibes",
          description: "Working from my favorite coffee shop today. The atmosphere here is perfect for creativity! ☕",
          media: {
            type: "image",
            url: "/images/feature-holographic-interface.jpg",
            thumbnail: "/images/feature-holographic-interface.jpg",
            aspectRatio: 16/9,
            alt: "Coffee shop interior",
          },
          activityType: "working",
          location: "Downtown Coffee Co.",
        },
        engagement: {
          likes: 19,
          comments: 3,
          shares: 0,
          isLiked: true,
          isCommented: false,
          isShared: false,
        },
        metadata: {
          showSwipeOption: false,
          compatibilityScore: 74,
          priority: "low",
          tags: ["coffee", "working", "creativity"],
          location: "Downtown Coffee Co.",
        },
      },
      // More Heart Games Achievements
      {
        id: "heart-games-3",
        type: "heart-games-achievement",
        userId: "user-13",
        username: "Casey",
        avatar: "/images/profile-casey.jpg",
        timestamp: new Date(Date.now() - 1000 * 60 * 70), // 1 hour 10 minutes ago
        content: {
          title: "New Achievement!",
          description: "Unlocked 'Empathy Master' in Heart Connections",
          gameType: "Heart Connections",
          achievement: {
            id: "achievement-2",
            name: "Empathy Master",
            description: "Show exceptional empathy in all scenarios",
            icon: "💝",
            rarity: "epic",
            unlockedAt: new Date(),
            category: "games",
            gameType: "Heart Connections",
            level: 8,
          },
          score: 98,
          level: 8,
        },
        engagement: {
          likes: 35,
          comments: 7,
          shares: 3,
          isLiked: false,
          isCommented: false,
          isShared: false,
        },
        metadata: {
          showSwipeOption: true,
          compatibilityScore: 91,
          priority: "high",
          tags: ["heart-games", "achievement", "empathy"],
        },
      },
      {
        id: "heart-games-4",
        type: "heart-games-completion",
        userId: "user-14",
        username: "Morgan",
        avatar: "/images/profile-morgan.jpg",
        timestamp: new Date(Date.now() - 1000 * 60 * 80), // 1 hour 20 minutes ago
        content: {
          title: "Perfect Score!",
          description: "Achieved 100% in Heart Choices - first time ever! 🎉",
          gameType: "Heart Choices",
          score: 100,
          level: 6,
          gameResult: {
            gameId: "heart-choices-2",
            gameType: "Heart Choices",
            score: 100,
            completedAt: new Date(),
            duration: 240,
            answers: [],
            insights: ["You have excellent decision-making skills", "Your values are well-defined"],
          },
        },
        engagement: {
          likes: 42,
          comments: 12,
          shares: 5,
          isLiked: true,
          isCommented: false,
          isShared: false,
        },
        metadata: {
          showSwipeOption: true,
          compatibilityScore: 88,
          priority: "high",
          tags: ["heart-games", "perfect-score", "achievement"],
        },
      },
      // Leaderboard Updates
      {
        id: "leaderboard-1",
        type: "leaderboard-update",
        userId: "user-15",
        username: "Quinn",
        avatar: "/images/profile-quinn.jpg",
        timestamp: new Date(Date.now() - 1000 * 60 * 90), // 1 hour 30 minutes ago
        content: {
          title: "Leaderboard Update",
          description: "Moved up to #3 in this week's Heart Games leaderboard! 🏆",
          leaderboard: {
            type: "weekly",
            category: "heart-games",
            position: 3,
            previousPosition: 7,
            points: 2450,
            change: "+4",
          },
        },
        engagement: {
          likes: 38,
          comments: 9,
          shares: 2,
          isLiked: false,
          isCommented: false,
          isShared: false,
        },
        metadata: {
          showSwipeOption: false,
          compatibilityScore: 85,
          priority: "high",
          tags: ["leaderboard", "achievement", "ranking"],
        },
      },
      // More Compatibility Suggestions
      {
        id: "compatibility-2",
        type: "compatibility-suggestion",
        userId: "user-16",
        username: "Avery",
        avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=800&h=1200&fit=crop&crop=faces",
        timestamp: new Date(Date.now() - 1000 * 60 * 100), // 1 hour 40 minutes ago
        content: {
          title: "Potential Match!",
          description: "You have 89% compatibility with this amazing person",
          compatibilityScore: 89,
          matchReasons: [
            "Both love outdoor activities",
            "Similar sense of humor",
            "Compatible life goals"
          ],
          sharedInterests: ["Hiking", "Photography", "Music", "Cooking"],
          conversationStarters: [
            "What's your favorite hiking trail?",
            "Do you play any instruments?"
          ],
          personalityAlignment: {
            mbtiCompatibility: 92,
            traitSimilarity: 87,
            traitComplementarity: 88,
            communicationStyle: 91,
          },
        },
        engagement: {
          likes: 26,
          comments: 6,
          shares: 1,
          isLiked: false,
          isCommented: false,
          isShared: false,
        },
        metadata: {
          showSwipeOption: true,
          compatibilityScore: 89,
          priority: "high",
          tags: ["compatibility", "match", "outdoor"],
        },
      },
      // Community Challenge Updates
      {
        id: "challenge-2",
        type: "community-challenge",
        userId: "user-17",
        username: "Blake",
        avatar: "/images/profile-blake.jpg",
        timestamp: new Date(Date.now() - 1000 * 60 * 110), // 1 hour 50 minutes ago
        content: {
          title: "Challenge Progress",
          description: "Halfway through the 'Heart Games Marathon' challenge!",
          challenge: {
            id: "challenge-2",
            title: "Heart Games Marathon",
            description: "Complete 5 different Heart Games this week",
            type: "weekly",
            startDate: new Date(),
            endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
            participants: 156,
            progress: 3,
            total: 5,
            reward: {
              xp: 500,
              badges: [],
              features: [],
              social: {
                profileHighlight: true,
                leaderboardPosition: 10,
                exclusiveContent: false,
                communityRecognition: true,
              },
            },
          },
        },
        engagement: {
          likes: 31,
          comments: 8,
          shares: 2,
          isLiked: true,
          isCommented: false,
          isShared: false,
        },
        metadata: {
          showSwipeOption: false,
          priority: "medium",
          tags: ["challenge", "progress", "community"],
        },
      },
      // Video Content
      {
        id: "social-video-1",
        type: "social-activity",
        userId: "user-18",
        username: "Drew",
        avatar: "/images/profile-drew.jpg",
        timestamp: new Date(Date.now() - 1000 * 60 * 120), // 2 hours ago
        content: {
          title: "Cooking time!",
          description: "Making my famous pasta carbonara tonight 🍝",
          media: {
            type: "video",
            url: "/videos/cooking-carbonara.mp4",
            thumbnail: "/images/feature-holographic-interface.jpg",
            aspectRatio: 9/16,
            alt: "Cooking pasta carbonara",
            duration: 45,
          },
          activityType: "cooking",
          recipe: "Pasta Carbonara",
        },
        engagement: {
          likes: 47,
          comments: 15,
          shares: 6,
          isLiked: false,
          isCommented: true,
          isShared: false,
        },
        metadata: {
          showSwipeOption: true,
          compatibilityScore: 82,
          priority: "high",
          tags: ["cooking", "video", "food"],
        },
      },
      // Additional Compatibility Suggestions for Matches Tab
      {
        id: "compatibility-3",
        type: "compatibility-suggestion",
        userId: "user-19",
        username: "Jordan",
        avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=800&h=1200&fit=crop&crop=faces",
        timestamp: new Date(Date.now() - 1000 * 60 * 20), // 20 minutes ago
        content: {
          title: "Great Match Found!",
          description: "You have 94% compatibility",
          compatibilityScore: 94,
          matchReasons: [
            "Shared love for music and art",
            "Similar lifestyle preferences",
            "Compatible communication styles"
          ],
          sharedInterests: ["Music", "Art", "Travel", "Photography"],
          conversationStarters: [
            "What's your favorite music genre?",
            "Have you been to any art galleries recently?"
          ],
          personalityAlignment: {
            mbtiCompatibility: 91,
            traitSimilarity: 88,
            traitComplementarity: 92,
            communicationStyle: 94,
          },
        },
        engagement: {
          likes: 18,
          comments: 4,
          shares: 1,
          isLiked: false,
          isCommented: false,
          isShared: false,
        },
        metadata: {
          showSwipeOption: true,
          compatibilityScore: 94,
          priority: "high",
          tags: ["compatibility", "match"],
        },
      },
      {
        id: "compatibility-4",
        type: "compatibility-suggestion",
        userId: "user-20",
        username: "Morgan",
        avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=800&h=1200&fit=crop&crop=faces",
        timestamp: new Date(Date.now() - 1000 * 60 * 60), // 1 hour ago
        content: {
          title: "Potential Match!",
          description: "You have 87% compatibility",
          compatibilityScore: 87,
          matchReasons: [
            "Both enjoy outdoor adventures",
            "Similar values and goals",
            "Great conversation chemistry"
          ],
          sharedInterests: ["Hiking", "Camping", "Fitness", "Nature"],
          conversationStarters: [
            "What's your favorite hiking spot?",
            "Do you prefer camping or glamping?"
          ],
          personalityAlignment: {
            mbtiCompatibility: 85,
            traitSimilarity: 89,
            traitComplementarity: 86,
            communicationStyle: 88,
          },
        },
        engagement: {
          likes: 21,
          comments: 7,
          shares: 2,
          isLiked: false,
          isCommented: false,
          isShared: false,
        },
        metadata: {
          showSwipeOption: true,
          compatibilityScore: 87,
          priority: "high",
          tags: ["compatibility", "match", "outdoor"],
        },
      },
      {
        id: "compatibility-5",
        type: "compatibility-suggestion",
        userId: "user-21",
        username: "Riley",
        avatar: "https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=800&h=1200&fit=crop&crop=faces",
        timestamp: new Date(Date.now() - 1000 * 60 * 90), // 1.5 hours ago
        content: {
          title: "Great Match Found!",
          description: "You have 91% compatibility",
          compatibilityScore: 91,
          matchReasons: [
            "Perfect personality match",
            "Shared passion for food and cooking",
            "Compatible life goals"
          ],
          sharedInterests: ["Cooking", "Food", "Travel", "Movies"],
          conversationStarters: [
            "What's your favorite cuisine?",
            "Do you enjoy cooking at home?"
          ],
          personalityAlignment: {
            mbtiCompatibility: 93,
            traitSimilarity: 90,
            traitComplementarity: 91,
            communicationStyle: 92,
          },
        },
        engagement: {
          likes: 24,
          comments: 6,
          shares: 1,
          isLiked: false,
          isCommented: false,
          isShared: false,
        },
        metadata: {
          showSwipeOption: true,
          compatibilityScore: 91,
          priority: "high",
          tags: ["compatibility", "match", "food"],
        },
      },
      {
        id: "compatibility-6",
        type: "compatibility-suggestion",
        userId: "user-22",
        username: "Casey",
        avatar: "https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=800&h=1200&fit=crop&crop=faces",
        timestamp: new Date(Date.now() - 1000 * 60 * 75), // 1.25 hours ago
        content: {
          title: "Potential Match!",
          description: "You have 88% compatibility",
          compatibilityScore: 88,
          matchReasons: [
            "Both love fitness and wellness",
            "Similar sense of humor",
            "Compatible communication styles"
          ],
          sharedInterests: ["Fitness", "Yoga", "Wellness", "Reading"],
          conversationStarters: [
            "What's your favorite workout routine?",
            "Do you practice yoga?"
          ],
          personalityAlignment: {
            mbtiCompatibility: 87,
            traitSimilarity: 88,
            traitComplementarity: 89,
            communicationStyle: 90,
          },
        },
        engagement: {
          likes: 19,
          comments: 5,
          shares: 1,
          isLiked: false,
          isCommented: false,
          isShared: false,
        },
        metadata: {
          showSwipeOption: true,
          compatibilityScore: 88,
          priority: "high",
          tags: ["compatibility", "match", "fitness"],
        },
      },
    ];

    // Apply filters
    let filteredItems = mockItems;

    if (filters.contentType && filters.contentType.length > 0) {
      filteredItems = filteredItems.filter(item => 
        filters.contentType!.includes(item.type)
      );
    }

    if (filters.showCompatibilityOnly) {
      filteredItems = filteredItems.filter(item => 
        item.type === "compatibility-suggestion"
      );
    }

    if (filters.showHeartGamesOnly) {
      filteredItems = filteredItems.filter(item => 
        item.type.includes("heart-games") || item.type === "streak-milestone"
      );
    }

    // Sort by timestamp (newest first)
    const sortedItems = filteredItems.sort((a, b) => 
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
    
    // Debug logging removed for cleaner console
    return sortedItems;
  }
}

export const mockFeedData = MockFeedData;
